import {
  DATASET_TRIGGER_CRITERION_LABELS,
  PersistedWorkflow,
} from '@cotera/sdk/core';
import { Icon, Text } from '@cotera/client/app/components/ui';
import type {
  WorkflowDatasetTriggerViewData,
  WorkflowTriggerRenderStrategy,
  WorkflowTriggerViewData,
} from '../types';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

function isDatasetTrigger(
  trigger: WorkflowTriggerViewData
): trigger is WorkflowDatasetTriggerViewData {
  return trigger.kind === PersistedWorkflowTriggerKind.Dataset;
}

/** e.g. "Values change · 2 fields" — the whole config in one line. */
function summarize(trigger: WorkflowDatasetTriggerViewData): string {
  const label = DATASET_TRIGGER_CRITERION_LABELS[trigger.criterion];
  if (trigger.criterion !== 'record_changed') {
    return `${label}`;
  }
  const watched = trigger.trackFields;
  if (watched === null) {
    return `${label} · any field`;
  }
  return `${label} · ${watched.length} field${watched.length === 1 ? '' : 's'}`;
}

export const datasetTriggerStrategy: WorkflowTriggerRenderStrategy = {
  triggerKind: PersistedWorkflowTriggerKind.Dataset,
  accent: 'dataset',
  badge: 'Dataset',
  matches: isDatasetTrigger,
  getTitle: (trigger) =>
    isDatasetTrigger(trigger) && trigger.name.trim() !== ''
      ? trigger.name
      : 'Dataset change',
  getDescription: (trigger) =>
    isDatasetTrigger(trigger) ? summarize(trigger) : undefined,
  renderIcon: () => <Icon icon="dataset" className="h-4 w-4" />,
  renderBody: (trigger) => {
    if (!isDatasetTrigger(trigger)) {
      return null;
    }
    return (
      <Text.Caption className="text-muted-foreground block truncate">
        {summarize(trigger)}
        {/* Ahead of the backfill note because it outranks it: a manual trigger
            has no backfill to mention, and "manual" is the thing a reader
            scanning the canvas for "why has this not run?" is looking for. */}
        {trigger.runMode === 'manual' ? ' · manual only' : ''}
        {trigger.processExistingRows ? ' · backfills' : ''}
      </Text.Caption>
    );
  },
};
